import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { ExportPhotosInput, ExportPhotosResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * exportPhotos callable hook (Sprint BB Part 3、REQ-4.3.14 写真 ZIP)。
 *
 * 旧 v1 の server-side ZIP 生成は新 dep (JSZip / archiver) 追加を回避するため
 * **signed URL list 返却方式** に切替。client は本 hook で URL list を受け取り、
 * 個別 `<a>` link or 別途 client-side JSZip-lite で zip 化する。
 */

export type UseExportPhotosState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: ExportPhotosResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseExportPhotosReturn {
  state: UseExportPhotosState;
  call: (input: ExportPhotosInput) => Promise<ExportPhotosResult | null>;
  reset: () => void;
}

export function useExportPhotos(): UseExportPhotosReturn {
  const [state, setState] = useState<UseExportPhotosState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: ExportPhotosInput): Promise<ExportPhotosResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<ExportPhotosInput, ExportPhotosResult>(functions, 'exportPhotos');
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
